import React, { useState, useEffect } from 'react';
import {
  Search, Users, Building, Settings, Filter, Eye, MoreHorizontal,
  MapPin, Briefcase, Award, Clock, CheckCircle, AlertCircle,
  ExternalLink, MessageSquare, Download, Star, Calendar
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { clientAPI } from '../../utils/api';

// Simple currency and date formatting functions
const formatCurrency = (amount, locale = 'en-AU') => {
  if (!amount && amount !== 0) return '';
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

const formatEmploymentDate = (month, year) => {
  if (!month || !year) return '';
  return `${String(month).padStart(2, '0')}/${year}`;
};

const ClientPortalDashboard = () => {
  const { user, logout } = useAuth();
  const [activeTab, setActiveTab] = useState('search');
  const [clientData, setClientData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    loadClientData();
  }, []);

  const loadClientData = async () => {
    try {
      setLoading(true);
      const response = await clientAPI.getProfile();
      setClientData(response.data.data);
    } catch (error) {
      setError('Failed to load profile data');
      console.error('Load client data error:', error);
    } finally {
      setLoading(false);
    }
  };

  const showMessage = (message, type = 'success') => {
    if (type === 'success') {
      setSuccess(message);
      setError('');
      setTimeout(() => setSuccess(''), 3000);
    } else {
      setError(message);
      setSuccess('');
      setTimeout(() => setError(''), 5000);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
      </div>
    );
  }

  const tabs = [
    { id: 'search', label: 'Search Candidates', icon: Search },
    { id: 'requests', label: 'My Requests', icon: MessageSquare },
    { id: 'profile', label: 'Company Profile', icon: Building },
    { id: 'settings', label: 'Settings', icon: Settings },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center">
              <img
                src="/appsytec-logo.png"
                alt="Appsytec Logo"
                className="h-8 w-auto object-contain mr-4"
              />
              <h1 className="text-2xl font-bold text-gray-900">Client Portal</h1>
            </div>
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-600">
                Welcome, {clientData?.company_name || user.email}
              </span>
              <button
                onClick={logout}
                className="bg-red-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-red-700"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-4">
          <div className="bg-red-50 border border-red-200 rounded-md p-4">
            <div className="flex">
              <AlertCircle className="h-5 w-5 text-red-400" />
              <div className="ml-3">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {success && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-4">
          <div className="bg-green-50 border border-green-200 rounded-md p-4">
            <div className="flex">
              <CheckCircle className="h-5 w-5 text-green-400" />
              <div className="ml-3">
                <p className="text-sm text-green-700">{success}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex">
          {/* Sidebar */}
          <div className="w-64 bg-white rounded-lg shadow-sm p-6 h-fit">
            <nav className="space-y-2">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-md ${
                      activeTab === tab.id
                        ? 'bg-red-600 text-white'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <Icon className="mr-3 h-5 w-5" />
                    {tab.label}
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Main Content */}
          <div className="flex-1 ml-8">
            <div className="bg-white rounded-lg shadow-sm">
              {activeTab === 'search' && (
                <CandidateSearchTab showMessage={showMessage} />
              )}
              {activeTab === 'requests' && (
                <RequestsTab showMessage={showMessage} />
              )}
              {activeTab === 'profile' && (
                <ProfileTab
                  clientData={clientData}
                  setClientData={setClientData}
                  showMessage={showMessage}
                />
              )}
              {activeTab === 'settings' && (
                <SettingsTab showMessage={showMessage} />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const CandidateSearchTab = ({ showMessage }) => {
  const [searchParams, setSearchParams] = useState({
    skills: '',
    location: '',
    clearance: '',
    experience_min: '',
    experience_max: '',
    job_titles: '',
    industries: '',
    page: 1,
    limit: 20
  });
  const [candidates, setCandidates] = useState([]);
  const [pagination, setPagination] = useState({});
  const [loading, setLoading] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [showCandidateModal, setShowCandidateModal] = useState(false);

  const handleSearch = async (resetPage = true) => {
    try {
      setLoading(true);
      const params = { ...searchParams };
      if (resetPage) params.page = 1;

      Object.keys(params).forEach(key => {
        if (!params[key]) delete params[key];
      });

      const response = await clientAPI.searchCandidates(params);
      setCandidates(response.data.data.candidates);
      setPagination(response.data.data.pagination);
    } catch (error) {
      showMessage('Failed to search candidates', 'error');
    } finally {
      setLoading(false);
    }
  };

  const viewCandidate = async (candidateId) => {
    try {
      const response = await clientAPI.getCandidate(candidateId);
      setSelectedCandidate(response.data.data);
      setShowCandidateModal(true);
    } catch (error) {
      showMessage('Failed to load candidate details', 'error');
    }
  };

  const requestMoreInfo = async (candidateId, message) => {
    try {
      await clientAPI.requestInfo(candidateId, message);
      showMessage('Information request sent successfully');
      setShowCandidateModal(false);
    } catch (error) {
      showMessage('Failed to send information request', 'error');
    }
  };

  const clearanceOptions = ['', 'BPSS', 'CTC', 'SC', 'DV'];

  return (
    <div className="p-6">
      <h2 className="text-xl font-semibold text-gray-900 mb-6">Search Candidates</h2>

      {/* Search Filters */}
      <div className="mb-6 p-4 bg-gray-50 rounded-lg">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Skills (comma-separated)
            </label>
            <input
              type="text"
              value={searchParams.skills}
              onChange={(e) => setSearchParams({ ...searchParams, skills: e.target.value })}
              placeholder="e.g. JavaScript, React, Python"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-red-500 focus:border-red-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Location
            </label>
            <input
              type="text"
              value={searchParams.location}
              onChange={(e) => setSearchParams({ ...searchParams, location: e.target.value })}
              placeholder="e.g. London, Manchester"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-red-500 focus:border-red-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Security Clearance
            </label>
            <select
              value={searchParams.clearance}
              onChange={(e) => setSearchParams({ ...searchParams, clearance: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-red-500 focus:border-red-500"
            >
              <option value="">Any clearance level</option>
              {clearanceOptions.slice(1).map(level => (
                <option key={level} value={level}>{level}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Min Experience (years)
            </label>
            <input
              type="number"
              min="0"
              value={searchParams.experience_min}
              onChange={(e) => setSearchParams({ ...searchParams, experience_min: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-red-500 focus:border-red-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Max Experience (years)
            </label>
            <input
              type="number"
              min="0"
              value={searchParams.experience_max}
              onChange={(e) => setSearchParams({ ...searchParams, experience_max: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-red-500 focus:border-red-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Job Titles (comma-separated)
            </label>
            <input
              type="text"
              value={searchParams.job_titles}
              onChange={(e) => setSearchParams({ ...searchParams, job_titles: e.target.value })}
              placeholder="e.g. Software Engineer, Developer"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-red-500 focus:border-red-500"
            />
          </div>
        </div>

        <div className="mt-4 flex space-x-2">
          <button
            onClick={() => handleSearch()}
            disabled={loading}
            className="flex items-center px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50"
          >
            <Search className="mr-2 h-4 w-4" />
            {loading ? 'Searching...' : 'Search'}
          </button>
          <button
            onClick={() => {
              setSearchParams({
                skills: '',
                location: '',
                clearance: '',
                experience_min: '',
                experience_max: '',
                job_titles: '',
                industries: '',
                page: 1,
                limit: 20
              });
              setCandidates([]);
              setPagination({});
            }}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
          >
            Clear Filters
          </button>
        </div>
      </div>

      {/* Search Results */}
      {candidates.length > 0 && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <p className="text-sm text-gray-600">
              Found {pagination.total} candidates
            </p>
          </div>

          <div className="space-y-4">
            {candidates.map((candidate) => (
              <CandidateCard
                key={candidate.id}
                candidate={candidate}
                onView={() => viewCandidate(candidate.id)}
              />
            ))}
          </div>

          {/* Pagination */}
          {pagination.pages > 1 && (
            <div className="mt-6 flex justify-center">
              <div className="flex space-x-1">
                {Array.from({ length: pagination.pages }, (_, i) => i + 1).map((page) => (
                  <button
                    key={page}
                    onClick={() => {
                      setSearchParams({ ...searchParams, page });
                      handleSearch(false);
                    }}
                    className={`px-3 py-1 text-sm rounded ${
                      page === pagination.page
                        ? 'bg-red-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {page}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Candidate Detail Modal */}
      {showCandidateModal && selectedCandidate && (
        <CandidateDetailModal
          candidate={selectedCandidate}
          onClose={() => setShowCandidateModal(false)}
          onRequestInfo={requestMoreInfo}
        />
      )}
    </div>
  );
};

const CandidateCard = ({ candidate, onView }) => {
  return (
    <div className="p-4 border border-gray-200 rounded-lg hover:shadow-md transition-shadow">
      <div className="flex justify-between items-start">
        <div className="flex-1">
          <div className="flex items-center mb-2">
            <h3 className="font-medium text-gray-900 mr-2">
              {candidate.first_name === '[REDACTED]' ? 'Candidate Profile' : `${candidate.first_name} ${candidate.last_name}`}
            </h3>
            {candidate.security_clearance && candidate.security_clearance !== '[REDACTED]' && (
              <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">
                {candidate.security_clearance}
              </span>
            )}
          </div>

          <div className="space-y-1 text-sm text-gray-600">
            {candidate.current_job_title && candidate.current_job_title !== '[REDACTED]' && (
              <p className="flex items-center">
                <Briefcase className="h-4 w-4 mr-1" />
                {candidate.current_job_title}
              </p>
            )}

            {candidate.current_location && candidate.current_location !== '[REDACTED]' && (
              <p className="flex items-center">
                <MapPin className="h-4 w-4 mr-1" />
                {candidate.current_location}
              </p>
            )}

            {candidate.years_experience && (
              <p className="flex items-center">
                <Clock className="h-4 w-4 mr-1" />
                {candidate.years_experience} years experience
              </p>
            )}

            {candidate.expected_salary && candidate.expected_salary !== '[REDACTED]' && (
              <p className="text-gray-700 font-medium">
                Expected: {formatCurrency(candidate.expected_salary)}
              </p>
            )}
          </div>
        </div>

        <div className="flex space-x-2">
          <button
            onClick={onView}
            className="flex items-center px-3 py-1 text-sm font-medium text-white bg-red-600 rounded hover:bg-red-700"
          >
            <Eye className="mr-1 h-4 w-4" />
            View
          </button>
        </div>
      </div>
    </div>
  );
};

const CandidateDetailModal = ({ candidate, onClose, onRequestInfo }) => {
  const [requestMessage, setRequestMessage] = useState('');
  const [sending, setSending] = useState(false);

  const handleRequestInfo = async () => {
    if (!requestMessage.trim()) return;

    setSending(true);
    await onRequestInfo(candidate.id, requestMessage);
    setSending(false);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg max-w-4xl max-h-[90vh] overflow-y-auto p-6 m-4">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold text-gray-900">
            {candidate.first_name === '[REDACTED]' ? 'Candidate Profile' : `${candidate.first_name} ${candidate.last_name}`}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <span className="sr-only">Close</span>
            ×
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div>
            <h3 className="font-medium text-gray-900 mb-3">Personal Information</h3>
            <div className="space-y-2 text-sm">
              {candidate.current_location && candidate.current_location !== '[REDACTED]' && (
                <p><span className="font-medium">Location:</span> {candidate.current_location}</p>
              )}
              {candidate.years_experience && (
                <p><span className="font-medium">Experience:</span> {candidate.years_experience} years</p>
              )}
              {candidate.security_clearance && candidate.security_clearance !== '[REDACTED]' && (
                <p><span className="font-medium">Security Clearance:</span> {candidate.security_clearance}</p>
              )}
              {candidate.expected_salary && candidate.expected_salary !== '[REDACTED]' && (
                <p><span className="font-medium">Expected Salary:</span> {formatCurrency(candidate.expected_salary)}</p>
              )}
            </div>
          </div>

          <div>
            <h3 className="font-medium text-gray-900 mb-3">Current Role</h3>
            <div className="space-y-2 text-sm">
              {candidate.current_job_title && candidate.current_job_title !== '[REDACTED]' && (
                <p><span className="font-medium">Job Title:</span> {candidate.current_job_title}</p>
              )}
              {candidate.current_employer && candidate.current_employer !== '[REDACTED]' && (
                <p><span className="font-medium">Employer:</span> {candidate.current_employer}</p>
              )}
            </div>
          </div>
        </div>

        {/* Skills */}
        {candidate.skills && candidate.skills.length > 0 && (
          <div className="mb-6">
            <h3 className="font-medium text-gray-900 mb-3">Skills</h3>
            <div className="flex flex-wrap gap-2">
              {candidate.skills.map((skill, index) => (
                <span
                  key={index}
                  className="px-2 py-1 bg-gray-100 text-gray-700 text-sm rounded"
                >
                  {skill.skill_name} ({skill.proficiency_level})
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Education */}
        {candidate.education && candidate.education.length > 0 && (
          <div className="mb-6">
            <h3 className="font-medium text-gray-900 mb-3">Education</h3>
            <div className="space-y-2">
              {candidate.education.map((edu, index) => (
                <div key={index} className="text-sm">
                  <p className="font-medium">{edu.degree} in {edu.field_of_study}</p>
                  <p className="text-gray-600">{edu.institution}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Employment History */}
        {candidate.employment_history && candidate.employment_history.length > 0 && (
          <div className="mb-6">
            <h3 className="font-medium text-gray-900 mb-3">Employment History</h3>
            <div className="space-y-4">
              {candidate.employment_history.map((job, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <h4 className="text-sm font-semibold text-gray-900">
                        {job.job_title !== '[REDACTED]' ? job.job_title : '[JOB TITLE REDACTED]'}
                      </h4>
                      <p className="text-sm text-gray-700 font-medium">
                        {job.employer !== '[REDACTED]' ? job.employer : '[EMPLOYER REDACTED]'}
                      </p>
                    </div>
                    <div className="text-right">
                      {(job.start_date_month && job.start_date_year) ? (
                        <p className="text-xs text-gray-500">
                          {formatEmploymentDate(job.start_date_month, job.start_date_year)}
                          {' - '}
                          {job.is_current
                            ? 'Present'
                            : (job.end_date_month && job.end_date_year
                                ? formatEmploymentDate(job.end_date_month, job.end_date_year)
                                : 'End date not specified')}
                        </p>
                      ) : (
                        <p className="text-xs text-gray-500">[DATES REDACTED]</p>
                      )}
                      {job.is_current && (
                        <span className="inline-flex px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full mt-1">
                          Current Role
                        </span>
                      )}
                    </div>
                  </div>

                  {job.responsibilities && job.responsibilities !== '[REDACTED]' && (
                    <div className="mb-2">
                      <p className="text-xs font-medium text-gray-600 mb-1">Responsibilities:</p>
                      <p className="text-xs text-gray-700">{job.responsibilities}</p>
                    </div>
                  )}

                  {job.technology_stack && job.technology_stack !== '[REDACTED]' && (
                    <div className="mb-2">
                      <p className="text-xs font-medium text-gray-600 mb-1">Technology Stack:</p>
                      <p className="text-xs text-gray-700">{job.technology_stack}</p>
                    </div>
                  )}

                  {job.achievements && job.achievements !== '[REDACTED]' && (
                    <div>
                      <p className="text-xs font-medium text-gray-600 mb-1">Key Achievements:</p>
                      <p className="text-xs text-gray-700">{job.achievements}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Request More Information */}
        <div className="border-t pt-6">
          <h3 className="font-medium text-gray-900 mb-3">Request More Information</h3>
          <textarea
            value={requestMessage}
            onChange={(e) => setRequestMessage(e.target.value)}
            placeholder="Please provide a message explaining what additional information you would like about this candidate..."
            rows={4}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-red-500 focus:border-red-500"
          />
          <div className="mt-4 flex justify-end space-x-2">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
            >
              Close
            </button>
            <button
              onClick={handleRequestInfo}
              disabled={!requestMessage.trim() || sending}
              className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50"
            >
              {sending ? 'Sending...' : 'Request Information'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const RequestsTab = ({ showMessage }) => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadRequests();
  }, []);

  const loadRequests = async () => {
    try {
      setLoading(true);
      const response = await clientAPI.getRequests();
      setRequests(response.data.data);
    } catch (error) {
      showMessage('Failed to load requests', 'error');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <h2 className="text-xl font-semibold text-gray-900 mb-6">My Information Requests</h2>

      {requests.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <MessageSquare className="mx-auto h-12 w-12 text-gray-300" />
          <p className="mt-2">No information requests yet</p>
        </div>
      ) : (
        <div className="space-y-4">
          {requests.map((request) => (
            <div key={request.id} className="p-4 border border-gray-200 rounded-lg">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <h3 className="font-medium text-gray-900">
                    {request.first_name} {request.last_name}
                  </h3>
                  <p className="text-sm text-gray-600">{request.current_job_title}</p>
                  <p className="text-sm text-gray-500 mt-1">{request.current_location}</p>

                  {request.request_message && (
                    <div className="mt-2 p-3 bg-gray-50 rounded text-sm">
                      <p className="font-medium text-gray-700">Your message:</p>
                      <p className="text-gray-600">{request.request_message}</p>
                    </div>
                  )}

                  {request.admin_response && (
                    <div className="mt-2 p-3 bg-blue-50 rounded text-sm">
                      <p className="font-medium text-blue-700">Admin response:</p>
                      <p className="text-blue-600">{request.admin_response}</p>
                    </div>
                  )}
                </div>

                <div className="ml-4">
                  <span className={`px-2 py-1 text-xs rounded ${
                    request.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                    request.status === 'approved' ? 'bg-green-100 text-green-800' :
                    'bg-red-100 text-red-800'
                  }`}>
                    {request.status}
                  </span>
                  <p className="text-xs text-gray-500 mt-1">
                    {new Date(request.requested_at).toLocaleDateString()}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const ProfileTab = ({ clientData, setClientData, showMessage }) => {
  // Implementation for client profile management
  return <div className="p-6">Client profile management coming soon...</div>;
};

const SettingsTab = ({ showMessage }) => {
  // Implementation for client settings
  return <div className="p-6">Client settings coming soon...</div>;
};

export default ClientPortalDashboard;